import { useEffect, useRef, useState, type ReactNode } from "react"
import {
  portalRequestAnchor,
  type PortalAnswerValue,
  type PortalClientFile,
  type PortalRequestResponse,
  type PortalSessionResponse,
} from "@ledgerbox/domain"

import { errorMessage } from "./api"
import { usePortalApi } from "./api-context"
import {
  countInWords,
  formatShortDate,
  plural,
  requestLabel,
} from "./format"
import { homeCopy } from "./home-copy"
import { AccountingAction, ExternalHandoff } from "./item-actions"
import { ItemExample } from "./item-example"
import {
  addFileLabel,
  isSentBack,
  canceledRequiredLetters,
  everythingAccepted,
  isCanceledLetter,
  needsClient,
  answerHasSendSlot,
  offersKeepAnswer,
  pillPressValue,
  takePhotoLabel,
  withShownItems,
  canUndoClientDone,
} from "./item-state"
import { useLandOnItem, type ItemToFix } from "./items-to-fix"
import { ItemThread, useItemThreads, type ItemThreads } from "./portal-threads"
import { useDraftMemory, useTextDraft } from "./use-item-draft"
import type { ItemDraft } from "./item-draft"
import {
  AmountAnswer,
  DraftNote,
  PickAnswer,
  SendOrClear,
  TableAnswer,
} from "./item-answer-fields"
import { groupBySharedDay } from "./portal-deliverables"
import { LazySigningScreen } from "./signing/lazy-signing-screen"
import { itemSigningLabel } from "./signing/signing-model"
import { TransactionQuestion } from "./transaction-question"
import {
  HiddenFileInput,
  InlineError,
  useCoarsePointer,
  useNarrowScreen,
} from "./ui"
import { type UploadItem } from "./upload"

type UploadTarget = { requestId: string; itemId: string }

type ChecklistProps = {
  session: PortalSessionResponse
  firmName: string
  uploads: UploadItem[]
  queueNote: string | null
  actionError: string | null
  onActionError(next: string | null): void
  onFiles(files: FileList | File[], target?: UploadTarget): Promise<void>
  onRetry(item: UploadItem): Promise<void>
  onDismissUpload(id: string): void
  onSessionChange(session: PortalSessionResponse): void
  onOpenDeliverables(): void
  onOpenHistory(): void
  /** The sent-back item the header button named. */
  itemToFix?: ItemToFix | null
  onLanded?(): void
}

const itemsIn = (request: PortalRequestResponse) =>
  request.items.filter((item) => item.status !== "open").length

/** The client can say they are done once every required item has arrived. The
 * firm accepting them comes later and is not the client's to wait for. */
const everythingRequiredIn = (request: PortalRequestResponse) =>
  request.progress.received +
    canceledRequiredLetters(withShownItems(request).items) >=
  request.progress.required

export function ChecklistView({
  session,
  firmName,
  uploads,
  queueNote,
  actionError,
  onActionError,
  onFiles,
  onRetry,
  onDismissUpload,
  onSessionChange,
  onOpenDeliverables,
  onOpenHistory,
  itemToFix,
  onLanded,
}: ChecklistProps) {
  const requests = session.requests.map(withShownItems)
  const single = requests.length === 1 ? requests[0] : null
  const total = requests.reduce((sum, request) => sum + request.items.length, 0)
  const done = requests.reduce((sum, request) => sum + itemsIn(request), 0)
  const left = total - done
  const allTold =
    requests.length > 0 && requests.every((request) => request.clientDoneAt)
  const dueAt = requests
    .map((request) => request.dueAt)
    .sort()
    .at(0)

  const api = usePortalApi()
  const own = homeCopy(session, firmName)
  const lede =
    own.lede ??
    checklistLede({
      requestCount: requests.length,
      left,
      allTold,
      firmName,
    })

  const replaceRequest = (request: PortalRequestResponse) =>
    onSessionChange({
      ...session,
      requests: session.requests.map((current) =>
        current.requestId === request.requestId ? request : current
      ),
    })
  const rememberDraft = useDraftMemory(session, onSessionChange)
  const threads = useItemThreads()
  const [signingId, setSigningId] = useState<string | null>(null)
  // A sent-back item is always on the open list, so it is already drawn.
  useLandOnItem(itemToFix, (itemId) => `ask-${itemId}`, onLanded)

  return (
    <div className="page checklist">
      <div className="page-heading">
        <h1>
          {own.heading ??
            (single ? requestLabel(single) : "Your documents")}
        </h1>
        {lede ? <p className="lede">{lede}</p> : null}
        {total > 0 ? (
          <ProgressTrack done={done} total={total} dueAt={dueAt ?? null} />
        ) : null}
      </div>

      <DeliveryRow firmName={firmName} onOpen={onOpenDeliverables} />

      {requests.length === 0 ? (
        <div className="empty-state view-empty">
          <strong>Nothing to send right now</strong>
          <span>
            {session.uploadAllowed
              ? `When ${firmName} needs something from you, it shows up here. You can still send a file below.`
              : `When ${firmName} needs something from you, it shows up here`}
          </span>
        </div>
      ) : null}

      {requests.map((request) => (
        <RequestPanel
          key={request.requestId}
          request={request}
          soleRequest={Boolean(single)}
          files={session.files}
          uploadAllowed={session.uploadAllowed}
          firmName={firmName}
          contactId={session.contact.contactId}
          onFiles={onFiles}
          onAnswered={replaceRequest}
          onDraftSaved={rememberDraft}
          onSign={setSigningId}
          onRefresh={() => api.getSession().then(onSessionChange)}
          threads={threads}
        />
      ))}

      {session.uploadAllowed ? (
        <SendWell onFiles={onFiles} />
      ) : (
        <div className="notice-card">
          <strong>You cannot add files right now</strong>
          <p>
            You can still read what you already sent. Ask {firmName} to turn
            uploads back on.
          </p>
        </div>
      )}

      {queueNote ? <p className="scoped-note">{queueNote}</p> : null}
      {uploads.length > 0 ? (
        <UploadQueue
          uploads={uploads}
          onRetry={(item) => void onRetry(item)}
          onDismiss={onDismissUpload}
        />
      ) : null}
      {actionError ? <InlineError message={actionError} /> : null}

      <p className="history-link">
        <button type="button" className="text-button" onClick={onOpenHistory}>
          Everything you have sent
        </button>
      </p>

      <LazySigningScreen
        signatureId={signingId ?? ""}
        open={signingId !== null}
        onClose={() => setSigningId(null)}
        // The screen already says it is signed; a list that fails to read
        // again catches up on the next visit.
        onSigned={() =>
          void api
            .getSession()
            .then(onSessionChange)
            .catch(() => undefined)
        }
      />

      {requests.map((request) => (
        <DoneAction
          key={`done-${request.requestId}`}
          request={request}
          firmName={firmName}
          onActionError={onActionError}
          onSessionChange={onSessionChange}
        />
      ))}
    </div>
  )
}

const checklistLede = (input: {
  requestCount: number
  left: number
  allTold: boolean
  firmName: string
}) => {
  const { requestCount, left, allTold, firmName } = input
  // The empty block under the heading says it, so the lede stays out.
  if (requestCount === 0) return null
  if (allTold) {
    return `You told ${firmName} you sent everything`
  }
  if (left === 0) {
    return `Everything is in. Tell ${firmName} you sent everything so they can start.`
  }
  if (left === 1) {
    return "One thing left. Add it here, or send it and the rest can follow."
  }
  const word = countInWords(left)
  return `${word.charAt(0).toUpperCase()}${word.slice(1)} things left. Add what you have now. The rest can follow.`
}

function ProgressTrack({
  done,
  total,
  dueAt,
}: {
  done: number
  total: number
  dueAt: string | null
}) {
  const percent = Math.round((done / total) * 100)
  return (
    <div className="progress">
      <div className="track" aria-hidden="true">
        <span style={{ width: `${percent}%` }} />
      </div>
      <p className="progress-say">
        <span>
          <b className="tabular">
            {done} of {total} sent.
          </b>{" "}
          {done === total
            ? "That is everything they asked for."
            : total - done === 1
              ? "Send the last one and you're done."
              : `Send the other ${total - done} and you're done.`}
        </span>
        {dueAt ? (
          <span className="mono">Due {formatShortDate(dueAt)}</span>
        ) : null}
      </p>
    </div>
  )
}

/* What the firm sent back leads the page: a client who came to read their
   financials should not have to hunt for them behind a checklist. */
function DeliveryRow({
  firmName,
  onOpen,
}: {
  firmName: string
  onOpen(): void
}) {
  const api = usePortalApi()
  const [delivery, setDelivery] = useState<{
    /** A named package ("August 2026 financial package"), when there is one. */
    title: string | null
    count: number
    sharedAt: string
  } | null>(null)

  useEffect(() => {
    let active = true
    // A package the firm published and loose files it shared both land under
    // For you; the home names the newer of the two the client has not
    // confirmed. Each read that fails is left out: a missing row is quieter
    // than an error a client cannot act on.
    void Promise.allSettled([
      api.listEngagementDeliveries(),
      api.listDeliverables(),
    ]).then(([packages, loose]) => {
      if (!active) return
      const newestPackage =
        packages.status === "fulfilled"
          ? packages.value.deliveries
              .filter((entry) => entry.acknowledgedByMe === false)
              .sort((a, b) => b.publishedAt.localeCompare(a.publishedAt))
              .at(0)
          : undefined
      // The newest day the firm sent on, read the same way the For you page
      // reads it.
      const waiting =
        loose.status === "fulfilled"
          ? loose.value.files.filter((file) => !file.acknowledged)
          : []
      const latest = groupBySharedDay(waiting).at(0)?.[1]
      const sharedAt = latest
        ?.map((file) => file.sharedAt)
        .sort()
        .at(-1)
      if (
        newestPackage &&
        (!sharedAt || newestPackage.publishedAt >= sharedAt)
      ) {
        setDelivery({
          title: newestPackage.title,
          count: newestPackage.files.length,
          sharedAt: newestPackage.publishedAt,
        })
        return
      }
      if (!latest || !sharedAt) return
      setDelivery({ title: null, count: latest.length, sharedAt })
    })
    return () => {
      active = false
    }
  }, [])

  if (!delivery) return null

  return (
    <div className="panel delivery-row">
      <div>
        <b>
          {delivery.title
            ? `${delivery.title} is ready for you`
            : delivery.count === 1
              ? "A file is ready for you"
              : `${delivery.count} files are ready for you`}
        </b>
        <span>
          Sent{" "}
          <span className="mono">{formatShortDate(delivery.sharedAt)}</span> by{" "}
          {firmName}
        </span>
      </div>
      <button type="button" className="ghost-pill" onClick={onOpen}>
        Open
      </button>
    </div>
  )
}

function RequestPanel({
  request,
  soleRequest,
  files,
  uploadAllowed,
  firmName,
  contactId,
  onFiles,
  onAnswered,
  onDraftSaved,
  onSign,
  onRefresh,
  threads,
}: {
  request: PortalRequestResponse
  soleRequest: boolean
  files: PortalClientFile[]
  uploadAllowed: boolean
  firmName: string
  contactId: string
  onFiles(files: FileList | File[], target?: UploadTarget): Promise<void>
  onAnswered(request: PortalRequestResponse): void
  onDraftSaved(itemId: string, draft: ItemDraft): void
  onSign(signatureId: string): void
  /** Reads the session again, after a step reported on another site. */
  onRefresh(): Promise<void>
  threads: ItemThreads
}) {
  const [showDone, setShowDone] = useState(false)
  const open = request.items.filter(needsClient)
  // A canceled letter says so on its row; it is neither to do nor sent.
  const canceled = request.items.filter(isCanceledLetter)
  const closed = request.items.filter(
    (item) => !needsClient(item) && !isCanceledLetter(item)
  )
  // The email's `#request-<id>` lands here.
  const titleId = portalRequestAnchor(request.requestId)

  const row = (item: PortalRequestResponse["items"][number]) => (
    <ChecklistItem
      key={item.itemId}
      item={item}
      files={files.filter((file) => file.requestItemId === item.itemId)}
      uploadAllowed={uploadAllowed}
      firmName={firmName}
      categoryChoices={request.categoryChoices}
      contactId={contactId}
      onFiles={(selected) =>
        void onFiles(selected, {
          requestId: request.requestId,
          itemId: item.itemId,
        })
      }
      onAnswered={onAnswered}
      onDraftSaved={(draft) => onDraftSaved(item.itemId, draft)}
      onSign={onSign}
      action={
        item.kind === "accounting_access" ? (
          <AccountingAction
            request={request}
            item={item}
            files={files.filter((file) => file.requestItemId === item.itemId)}
            firmName={firmName}
            uploadAllowed={uploadAllowed && !request.clientDoneAt}
            onFiles={onFiles}
            onRefresh={onRefresh}
          />
        ) : item.kind === "external_handoff" ? (
          <ExternalHandoff request={request} item={item} onRefresh={onRefresh} />
        ) : null
      }
      showSentBack
      thread={
        <ItemThread
          item={item}
          thread={threads.threadFor(item.itemId)}
          firmName={firmName}
          collapsed
          onPosted={() => void threads.refresh()}
        />
      }
    />
  )

  return (
    <section className="panel" aria-labelledby={titleId}>
      <div className="panel-head">
        {/* One request needs no name here; the page is already named after
            it. Several are told apart by their own names. */}
        <h2 id={titleId}>
          {soleRequest ? "Still needed" : requestLabel(request)}
        </h2>
        <span className="panel-meta tabular">
          {soleRequest
            ? `${open.length} ${plural(open.length, "item", "items")}`
            : `${closed.length} of ${request.items.length} sent${request.dueAt ? ` · due ${formatShortDate(request.dueAt)}` : ""}`}
        </span>
      </div>

      {open.length > 0 ? (
        <div className="ask-list">{open.map(row)}</div>
      ) : (
        <p className="panel-note">You sent everything {firmName} asked for</p>
      )}
      {canceled.length > 0 ? (
        <div className="ask-list">{canceled.map(row)}</div>
      ) : null}

      {closed.length > 0 ? (
        <>
          <div className="stack-row">
            <div>
              <b className="tabular">{closed.length} sent</b>
              <span>{closed.map((item) => item.name).join(", ")}</span>
            </div>
            <button
              type="button"
              className="ghost-pill"
              aria-expanded={showDone}
              aria-controls={showDone ? `${titleId}-done` : undefined}
              onClick={() => setShowDone((current) => !current)}
            >
              {showDone ? "Hide" : "Show"}
            </button>
          </div>
          {showDone ? (
            <div className="ask-list is-done" id={`${titleId}-done`}>
              {closed.map(row)}
            </div>
          ) : null}
        </>
      ) : null}
    </section>
  )
}

export function ChecklistItem({
  item,
  files,
  uploadAllowed,
  firmName,
  categoryChoices = [],
  contactId,
  onFiles,
  onAnswered,
  onDraftSaved,
  onSign,
  showSentBack = false,
  filePills = true,
  heading = true,
  thread,
  action = null,
}: {
  item: PortalRequestResponse["items"][number]
  files: PortalClientFile[]
  uploadAllowed: boolean
  firmName: string
  /** The firm's categories, for a question about transactions. */
  categoryChoices?: readonly { id: string; name: string }[]
  /** The signed-in contact, who may be the one to sign a document. */
  contactId: string
  onFiles(files: FileList): void
  onAnswered(request: PortalRequestResponse): void
  onDraftSaved(draft: ItemDraft): void
  /** Opens the signing screen, which the page holds: a signed item moves
      out of the open list, and the screen stays up to say it is done. */
  onSign(signatureId: string): void
  /** The item's own row says it was sent back; the engagement row, which
      stays on screen above it, says it there instead. */
  showSentBack?: boolean
  /** The item's files by name. The engagement task lists them itself, with
      the state of each, so it turns these off. */
  filePills?: boolean
  /** The item's name and its tick. The engagement task row above an open
      item already shows both, so it turns them off. */
  heading?: boolean
  /** The conversation about the item, under its answer. */
  thread?: ReactNode
  /** Accounting access or a link to another site: the step the client
      takes, in place of an answer. */
  action?: ReactNode
}) {
  const api = usePortalApi()
  const [pending, setPending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const draft = useTextDraft(item, onDraftSaved)
  const fileInput = useRef<HTMLInputElement>(null)
  const photoInput = useRef<HTMLInputElement>(null)
  const coarsePointer = useCoarsePointer()
  const done = item.status !== "open"

  if (item.kind === "transaction_question") {
    return (
      <TransactionQuestion
        item={item}
        categoryChoices={categoryChoices}
        firmName={firmName}
        files={files}
        uploadAllowed={uploadAllowed}
        onFiles={onFiles}
        onAnswered={onAnswered}
        filePills={filePills}
        heading={heading}
        showSentBack={showSentBack}
        thread={thread}
      />
    )
  }

  const saveAnswer = async (nextValue: PortalAnswerValue | null) => {
    setPending(true)
    setError(null)
    try {
      await draft.settle()
      onAnswered(await api.answerItem(item.itemId, nextValue))
    } catch (answerError) {
      setError(
        errorMessage(
          answerError instanceof Error
            ? answerError
            : new Error("Something went wrong. Try again.")
        )
      )
    } finally {
      setPending(false)
    }
  }

  const fileLabel = addFileLabel(item, uploadAllowed)
  const photoLabel = takePhotoLabel(item, uploadAllowed, coarsePointer)
  // A document to sign takes no answer and no file; its signer opens the
  // signing screen, everyone else reads who signs.
  const signature = item.signature ?? null
  const youSign = signature?.signerContactId === contactId
  const canSign = signature?.status === "awaiting_signature" && youSign

  /* Yes/No and a list of options are one control: pills that hold the answer,
     and a second press on the held one takes it back (on a sent-back item
     it sends the same answer again). A choice that takes several answers is
     PickAnswer's. */
  const choices: Array<{ value: string | boolean; label: string }> | null =
    item.kind === "yes_no"
      ? [
          { value: true, label: "Yes" },
          { value: false, label: "No" },
        ]
      : item.kind === "choice" && !item.allowMultiple
        ? item.options.map((option) => ({ value: option, label: option }))
        : null

  // The engagement task row shows "Sent back" itself, so only the checklist
  // draws the reason here; the answer controls read it either way (R3-B2).
  const sentBack = showSentBack && isSentBack(item)
  const answerSentBack = isSentBack(item)
  return (
    <article
      id={`ask-${item.itemId}`}
      className="ask-item"
      role="group"
      aria-label={item.name}
      // The items-to-fix button moves focus here.
      tabIndex={-1}
    >
      {heading ? (
        <span
          className={`tick${done && !sentBack ? " is-in" : ""}`}
          aria-hidden="true"
        />
      ) : null}
      <div className="ask-main">
        {heading ? <b>{item.name}</b> : null}
        {sentBack ? (
          <span className="sent-back">
            <b>Sent back</b>
            <span>{item.rejectionReason}</span>
          </span>
        ) : null}
        {item.description ? (
          <span className="d">{item.description}</span>
        ) : null}
        {item.required ? null : <span className="d">Only if you have it.</span>}
        <ItemExample item={item} />

        {item.kind === "accounting_access" || item.kind === "external_handoff" ? (
          action
        ) : item.kind === "document" ? (
          <>
            {filePills && files.length > 0 ? (
              <div className="file-pills">
                {files.map((file) => (
                  <span
                    className="file-pill mono"
                    key={file.fileId}
                    title={file.fileName}
                  >
                    {file.fileName}
                  </span>
                ))}
              </div>
            ) : null}
            <HiddenFileInput
              ref={fileInput}
              multiple={item.allowMultiple}
              onFiles={onFiles}
            />
            {photoLabel ? (
              <HiddenFileInput ref={photoInput} camera onFiles={onFiles} />
            ) : null}
          </>
        ) : item.kind === "signature" ? (
          signature && !canSign ? (
            <>
              <span className="d">
                {itemSigningLabel({
                  signature,
                  youAreSigner: youSign,
                  firmName,
                  formatDay: formatShortDate,
                })}
              </span>
              {/* The signed copy opens from the item too, not only from
                  For you (R8-U8). */}
              {signature.status === "signed" && signature.signedFileId ? (
                <SignedCopyLink fileId={signature.signedFileId} />
              ) : null}
            </>
          ) : null
        ) : item.kind === "checklist" ||
          (item.kind === "choice" && item.allowMultiple) ? (
          <PickAnswer item={item} pending={pending} onSave={saveAnswer} />
        ) : item.kind === "amount" ? (
          <AmountAnswer
            item={item}
            draft={draft}
            pending={pending}
            onSave={saveAnswer}
          />
        ) : item.kind === "table" ? (
          <TableAnswer
            item={item}
            pending={pending}
            onDraftSaved={onDraftSaved}
            onSave={saveAnswer}
          />
        ) : choices ? (
          <div className="answer-pills" role="group" aria-label={item.name}>
            {choices.map((choice) => (
              <button
                key={String(choice.value)}
                type="button"
                className="choice-pill"
                aria-pressed={item.answer?.value === choice.value}
                disabled={pending}
                onClick={() =>
                  void saveAnswer(
                    pillPressValue(
                      item.answer?.value,
                      choice.value,
                      answerSentBack
                    )
                  )
                }
              >
                {choice.label}
              </button>
            ))}
          </div>
        ) : item.kind === "date" ? (
          <div className="answer-field">
            {/* A date is whole the moment it is picked, so it is sent as
                the answer then and needs no draft. */}
            <input
              aria-label={item.name}
              type="date"
              value={draft.answered}
              disabled={pending}
              onChange={(event) => {
                const next = event.currentTarget.value
                if (next && next !== draft.answered) void saveAnswer(next)
              }}
            />
            {item.answer && !answerSentBack ? (
              <button
                type="button"
                className="ghost-pill"
                disabled={pending}
                onClick={() => void saveAnswer(null)}
              >
                Take back my answer
              </button>
            ) : null}
          </div>
        ) : (
          <form
            className="answer-field"
            onSubmit={(event) => {
              event.preventDefault()
              void saveAnswer(draft.value)
            }}
          >
            <textarea
              aria-label={item.name}
              aria-describedby={`draft-${item.itemId}`}
              maxLength={2000}
              rows={2}
              placeholder="Type your answer"
              value={draft.value}
              onChange={(event) => draft.change(event.currentTarget.value)}
            />
            <SendOrClear
              same={
                draft.value.trim() === draft.answered.trim() &&
                Boolean(item.answer)
              }
              pending={pending}
              disabled={
                !draft.value.trim() ||
                draft.value.trim() === draft.answered.trim()
              }
              onClear={() => void saveAnswer(null)}
              onKeep={
                answerSentBack
                  ? () => void saveAnswer(item.answer?.value ?? null)
                  : undefined
              }
            />
            <DraftNote
              id={`draft-${item.itemId}`}
              status={draft.status}
              value={draft.value}
              answered={draft.answered}
            />
          </form>
        )}
        {/* Amount, table and text answers keep theirs in Send's slot; the
            rest get the button under the answer (R9-U7). */}
        {offersKeepAnswer(item, answerSentBack) &&
        item.answer &&
        !answerHasSendSlot(item) ? (
          <div className="keep-answer">
            <button
              type="button"
              className="ghost-pill"
              disabled={pending}
              onClick={() => void saveAnswer(item.answer?.value ?? null)}
            >
              Keep my answer
            </button>
          </div>
        ) : null}
        {error ? <InlineError message={error} /> : null}
        {thread}
      </div>
      {fileLabel ? (
        <div className="ask-action">
          <button
            type="button"
            className="ghost-pill"
            onClick={() => fileInput.current?.click()}
          >
            {fileLabel}
          </button>
          {photoLabel ? (
            <button
              type="button"
              className="ghost-pill"
              onClick={() => photoInput.current?.click()}
            >
              {photoLabel}
            </button>
          ) : null}
        </div>
      ) : null}
      {signature && canSign ? (
        <div className="ask-action">
          <button
            type="button"
            className="ghost-pill"
            onClick={() => onSign(signature.signatureId)}
          >
            {itemSigningLabel({ signature, youAreSigner: true, firmName })}
          </button>
        </div>
      ) : null}
    </article>
  )
}

/* Opens the signed copy of a letter in a new tab. */
function SignedCopyLink({ fileId }: { fileId: string }) {
  const api = usePortalApi()
  const [error, setError] = useState<string | null>(null)
  const open = async () => {
    setError(null)
    // The tab opens on the press, before the await, so a browser that blocks
    // pop-ups after an await still lets it through.
    const opened = window.open("", "_blank")
    try {
      const access = await api.getDeliverableAccess(fileId)
      if (opened) {
        opened.opener = null
        opened.location.href = access.accessUrl
      } else window.location.assign(access.accessUrl)
    } catch {
      opened?.close()
      setError("The signed copy could not be opened. Try again.")
    }
  }
  return (
    <>
      <button
        type="button"
        className="text-button signed-copy-link"
        onClick={() => void open()}
      >
        Open the signed copy
      </button>
      {error ? <InlineError message={error} /> : null}
    </>
  )
}

/* Anything that is not on the list still has a home. A solid well, never a
   dashed outline, and the whole page takes the drop. */
export function SendWell({
  onFiles,
}: {
  onFiles(files: FileList | File[]): Promise<void>
}) {
  const [dragging, setDragging] = useState(false)
  const input = useRef<HTMLInputElement>(null)
  // Held in a ref because re-subscribing mid-drag would reset the depth count.
  const latestOnFiles = useRef(onFiles)
  latestOnFiles.current = onFiles

  useEffect(() => {
    const carriesFiles = (event: DragEvent) =>
      event.dataTransfer?.types.includes("Files") ?? false
    let depth = 0
    const enter = (event: DragEvent) => {
      if (!carriesFiles(event)) return
      depth += 1
      setDragging(true)
    }
    const over = (event: DragEvent) => {
      if (carriesFiles(event)) event.preventDefault()
    }
    const leave = (event: DragEvent) => {
      if (!carriesFiles(event)) return
      depth = Math.max(0, depth - 1)
      if (depth === 0) setDragging(false)
    }
    const drop = (event: DragEvent) => {
      if (!carriesFiles(event)) return
      event.preventDefault()
      depth = 0
      setDragging(false)
      if (event.dataTransfer?.files.length) {
        void latestOnFiles.current(event.dataTransfer.files)
      }
    }
    const handlers = {
      dragenter: enter,
      dragover: over,
      dragleave: leave,
      drop,
    }
    for (const [type, handler] of Object.entries(handlers)) {
      // SAFETY: every entry of `handlers` is keyed by the drag event it
      // handles, so the listener is only ever called with that event.
      window.addEventListener(type, handler as EventListener)
    }
    return () => {
      for (const [type, handler] of Object.entries(handlers)) {
        // SAFETY: the same map, so removal names the listener that was added.
        window.removeEventListener(type, handler as EventListener)
      }
    }
  }, [])

  // A phone has nothing to drag from, so it is not told to drag.
  const touch = useCoarsePointer()
  const narrow = useNarrowScreen()
  return (
    <div className={`send-well${dragging ? " is-dragging" : ""}`}>
      <b>Something else to send?</b>
      <span>
        {dragging
          ? "Let go and the files go to your firm"
          : touch || narrow
            ? "Choose files from this device"
            : "Drag files anywhere on this page, or choose them"}
      </span>
      <button
        type="button"
        className="ghost-pill"
        onClick={() => input.current?.click()}
      >
        Choose files
      </button>
      <HiddenFileInput
        ref={input}
        multiple
        onFiles={(files) => void onFiles(files)}
      />
    </div>
  )
}

/* The files this visit sent, one row each. The engagement workbench also
   lets a sent row go, because its list stays up for the whole task. */
export function UploadQueue({
  uploads,
  onRetry,
  onDismiss,
  dismissSent = false,
}: {
  uploads: UploadItem[]
  onRetry: (item: UploadItem) => void
  onDismiss: (id: string) => void
  dismissSent?: boolean
}) {
  return (
    <div className="upload-queue">
      {uploads.map((upload) => {
        const percent = Math.round(upload.progress * 100)
        const state = {
          preparing: "Getting ready…",
          uploading: `${percent}%`,
          received: "Sent",
          failed: "Not sent",
        }[upload.state]
        return (
          <div key={upload.id} className="queue-row">
            <div className="queue-details">
              <strong className="mono">{upload.name}</strong>
              {/* A sent file needs no line of its own: the state says Sent. */}
              {upload.state === "failed" ? (
                <span className="queue-error">{upload.message}</span>
              ) : upload.state === "received" ? null : (
                <div className="progress-track">
                  <span style={{ width: `${percent}%` }} />
                </div>
              )}
            </div>
            <span className="queue-state tabular">{state}</span>
            {upload.state === "failed" ? (
              <div className="queue-actions">
                <button
                  type="button"
                  className="text-button"
                  onClick={() => onRetry(upload)}
                >
                  Try again
                </button>
                <button
                  type="button"
                  className="text-button"
                  onClick={() => onDismiss(upload.id)}
                >
                  Dismiss
                </button>
              </div>
            ) : upload.state === "received" && dismissSent ? (
              <div className="queue-actions">
                <button
                  type="button"
                  className="text-button"
                  onClick={() => onDismiss(upload.id)}
                >
                  Dismiss
                </button>
              </div>
            ) : null}
          </div>
        )
      })}
    </div>
  )
}

/* The one filled pill a client sees. It turns on when every required item is
   in, because that is the moment the firm can start. */
function DoneAction({
  request,
  firmName,
  onActionError,
  onSessionChange,
}: {
  request: PortalRequestResponse
  firmName: string
  onActionError(next: string | null): void
  onSessionChange(session: PortalSessionResponse): void
}) {
  const api = usePortalApi()
  const [pending, setPending] = useState(false)
  const label = requestLabel(request)
  const ready = everythingRequiredIn(request)

  const act = async (undo: boolean) => {
    setPending(true)
    onActionError(null)
    try {
      onSessionChange(
        undo
          ? await api.undoRequestDone(request.requestId)
          : await api.markRequestDone(request.requestId)
      )
    } catch (error) {
      onActionError(
        errorMessage(
          error instanceof Error
            ? error
            : new Error("Something went wrong. Try again.")
        )
      )
    } finally {
      setPending(false)
    }
  }

  // The firm already has everything: nothing to tell them (R8-F2).
  if (!request.clientDoneAt && everythingAccepted(request.items)) {
    return (
      <div className="checklist-done is-told">
        <p>
          {firmName} has everything it needs for {label}
        </p>
      </div>
    )
  }

  // Undo stays until the firm closes the request.
  if (request.clientDoneAt) {
    return (
      <div className="checklist-done is-told">
        <p>
          You told {firmName} you sent everything for {label}. They take it from
          here.
        </p>
        {!canUndoClientDone(request) ? null : (
          <button
            type="button"
            className="ghost-pill"
            disabled={pending}
            onClick={() => void act(true)}
          >
            Undo
          </button>
        )}
      </div>
    )
  }

  return (
    <div className={`checklist-done${ready ? " is-ready" : ""}`}>
      <p>
        {ready
          ? `This tells ${firmName} you have nothing more to add`
          : `This turns on when everything ${firmName} needs is in. If one of them does not exist, say so in Messages.`}
      </p>
      <button
        type="button"
        className="accent-pill"
        disabled={!ready || pending}
        onClick={() => void act(false)}
      >
        {pending ? "Telling them…" : "I've sent everything"}
      </button>
    </div>
  )
}
